import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import {
  PriceIndexLevel,
  MarketTrendsResponse,
  MARKET_TRENDS_LINE_COLOURS,
} from '@propertypal/shared/src/types/marketTrends';
import { roundOff } from '@propertypal/shared/src/utils/number';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import { formatMarketTrends, formatPropertyAreaHistory } from '@propertypal/shared/src/utils/property/chartUtils';
import {
  LineChartContainer,
  LineChartHeader,
  LineChartWrapper,
} from '@propertypal/web-app/src/components/price-tracker/MarketTrends.style';
import {
  LoadingBox,
  Content,
  MarketTrendsCheckboxLabel,
  StatsContent,
  StyleStats,
} from '@propertypal/web-app/src/components/property/MarketTrendsModal.style';
import React, { useEffect, useMemo, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import LineChart from '../charts/LineChart';
import Checkbox from '../inputs/Checkbox';
import { Box } from '../layout';
import useMediaQuery from '../media-query/useMediaQuery';
import { Checkboxes } from '../property/AreaPriceHistory.style';
import { SubHeading, Text } from '../typography';

const MAX_POINTS = 24;
const GROWTH_WIDTH = 420;
const NON_GROWTH_WIDTH = 40;
const GROWTH_CHART_WIDTH = 1280 - 360;
const NON_GROWTH_CHART_WIDTH = 1280 - 40;
const MOBILE_CHART_SPACE = 50;
const MOBILE_CHART_HEIGHT = 350;
const CHART_HEIGHT = 450;

interface MarketTrendsChartsProps {
  data: MarketTrendsResponse | null;
  loading?: boolean;
  error?: any;
  councilArea: boolean;
  electoralArea: boolean;
  outcode: boolean;
}

const MarketTrendsCharts: React.FC<MarketTrendsChartsProps> = ({
  loading,
  error,
  data,
  councilArea,
  electoralArea,
  outcode,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const windowSize = useWindowSize();

  const [propertyTypes, setPropertyTypes] = useState<string[]>([]);
  const [bedrooms, setBedrooms] = useState<string[]>([]);

  const { points, selected } = useMemo(() => {
    let s: PriceIndexLevel = 'COUNTRY';
    if (councilArea) s = 'COUCIL_AREA';
    if (electoralArea) s = 'ELECTORAL_AREA';
    if (outcode) s = 'OUTCODE';
    return { points: data?.data?.find((p) => p.level === s), selected: s };
  }, [data?.data, councilArea, electoralArea, outcode]);

  const propertyTypeOpts = useMemo(() => {
    if (data?.data) return data.data.filter((d) => d.filterType === 'STYLE');
    return [];
  }, [data?.data]);

  const bedroomOpts = useMemo(() => {
    if (data?.data) return data.data.filter((d) => d.filterType === 'BEDS');
    return [];
  }, [data?.data]);

  const lineData = useMemo(() => {
    if (points && selected) return formatPropertyAreaHistory([points], [selected], MAX_POINTS, theme.primary);
    return null;
  }, [points, selected]);

  const propertyTypesLineData = useMemo(() => {
    if (propertyTypeOpts && propertyTypes.length)
      return formatMarketTrends(propertyTypeOpts, propertyTypes, MAX_POINTS);
    return null;
  }, [JSON.stringify(propertyTypeOpts), JSON.stringify(propertyTypes)]);

  const bedroomsLineData = useMemo(() => {
    if (bedroomOpts && bedrooms.length) return formatMarketTrends(bedroomOpts, bedrooms, MAX_POINTS);
    return null;
  }, [JSON.stringify(bedroomOpts), JSON.stringify(bedrooms)]);

  const { propertyTypesGrowth, bedroomsGrowth } = useMemo(() => {
    if (data?.averagePriceGrowth?.length)
      return {
        propertyTypesGrowth: data?.averagePriceGrowth?.filter((a) => a.type === 'STYLE'),
        bedroomsGrowth: data?.averagePriceGrowth?.filter((a) => a.type === 'BEDS'),
      };
    return { propertyTypesGrowth: [], bedroomsGrowth: [] };
  }, [JSON.stringify(data?.averagePriceGrowth)]);

  useEffect(() => {
    if ((!propertyTypes || !propertyTypes?.length) && propertyTypeOpts.length && propertyTypeOpts[0].label)
      setPropertyTypes([propertyTypeOpts[0].label]);
  }, [JSON.stringify(propertyTypeOpts)]);

  useEffect(() => {
    if ((!bedrooms || !bedrooms?.length) && bedroomOpts.length && bedroomOpts[0].label) {
      // default to 3 bed
      setBedrooms([bedroomOpts?.find((o) => o.label?.toLowerCase() === '3 bed')?.label || bedroomOpts[0].label]);
    }
  }, [JSON.stringify(bedroomOpts)]);

  const areaText = useMemo(() => {
    if (lineData?.length) {
      return {
        key: lineData?.[0]?.key,
        from: lineData?.[0]?.data?.[0]?.key,
        to: lineData[0].data[lineData[0].data!.length - 1].key,
      };
    }
    return null;
  }, [JSON.stringify(lineData)]);

  const propertyTypeText = useMemo(() => {
    if (propertyTypesLineData?.length) {
      const from = propertyTypesLineData?.[0]?.data?.[0]?.key;
      const to = propertyTypesLineData[0].data[propertyTypesLineData[0].data!.length - 1].key;
      return `Average Prices in ${areaText?.key || ''}, ${from} - ${to}`;
    }
    return `Average Prices in ${areaText?.key || ''} by Property Type`;
  }, [JSON.stringify(propertyTypesLineData), areaText?.key]);

  const bedroomText = useMemo(() => {
    if (bedroomsLineData?.length) {
      const from = bedroomsLineData?.[0]?.data?.[0]?.key;
      const to = bedroomsLineData[0].data[bedroomsLineData[0].data!.length - 1].key;
      return `Average Prices in ${areaText?.key} by Number of Bedrooms, ${from} - ${to}`;
    }
    return `Average Prices in ${areaText?.key} by Number of Bedrooms`;
  }, [JSON.stringify(bedroomsLineData), areaText?.key]);

  const chartHeight = isMobile ? MOBILE_CHART_HEIGHT : CHART_HEIGHT;

  if (windowSize.width === undefined || loading)
    return (
      <LoadingBox data-testid="marketTrendsLoading" minHeight={chartHeight + 55}>
        <Loader color={theme.backgroundLight} />
        <Text mt={15}>Loading Northern Ireland House Price Data</Text>
      </LoadingBox>
    );

  if (error || !data)
    return (
      <LoadingBox data-testid="market-trends-error" bg={theme.backgroundLighter} minHeight={chartHeight + 55}>
        <Text mt={15}>Northern Ireland House Prices Data Unavailable</Text>
      </LoadingBox>
    );

  const propertyTypeChartWidth = isMobile
    ? windowSize.width - MOBILE_CHART_SPACE
    : Math.min(
        propertyTypesGrowth.length ? GROWTH_CHART_WIDTH : NON_GROWTH_CHART_WIDTH,
        windowSize.width - (propertyTypesGrowth.length ? GROWTH_WIDTH : NON_GROWTH_WIDTH),
      );
  const bedroomsChartWidth = isMobile
    ? windowSize.width - MOBILE_CHART_SPACE
    : Math.min(
        bedroomsGrowth.length ? GROWTH_CHART_WIDTH : NON_GROWTH_CHART_WIDTH,
        windowSize.width - (bedroomsGrowth.length ? GROWTH_WIDTH : NON_GROWTH_WIDTH),
      );
  const propertyTypeChartHeight = isMobile ? MOBILE_CHART_HEIGHT : propertyTypesGrowth.length ? 550 : CHART_HEIGHT;
  const bedroomsChartHeight = isMobile ? MOBILE_CHART_HEIGHT : bedroomsGrowth.length ? 500 : CHART_HEIGHT;

  return (
    <div>
      <LineChartHeader width={propertyTypeChartWidth}>
        <SubHeading
          fontWeight="normal"
          color={theme.primary}
          align="center"
          data-testid="property-types-heading"
          mb={20}
        >
          {propertyTypeText}
        </SubHeading>
        <Checkboxes>
          {propertyTypeOpts.map(({ label }, i) => (
            <Checkbox
              key={label}
              name={label!}
              onValueChange={(checked) =>
                setPropertyTypes((prev) => {
                  if (checked) return [...prev, label!];
                  return prev.filter((item) => item !== label!);
                })
              }
              value={propertyTypes.includes(label!)}
              label={
                <MarketTrendsCheckboxLabel colour={MARKET_TRENDS_LINE_COLOURS[i]}>{label}</MarketTrendsCheckboxLabel>
              }
              testID={`${label}-propertyType-checkbox`}
            />
          ))}
        </Checkboxes>
      </LineChartHeader>
      <Content mt={40} mobGap={20}>
        <LineChartContainer>
          {!propertyTypesLineData?.length ? (
            <Box mt={propertyTypesGrowth.length ? 0 : 20}>
              <LoadingBox
                data-testid="propertyType-no-data"
                bg={theme.backgroundLighter}
                minHeight={propertyTypeChartHeight + 25}
              >
                <Text mt={15} mb={15} align="center">
                  Select a property type to view average price history
                </Text>
              </LoadingBox>
            </Box>
          ) : (
            <LineChartWrapper
              data-testid={`propertyTypes-line-chart-${selected}`}
              hasGrowth={!!propertyTypesGrowth.length}
            >
              <LineChart
                bubbleTextFormatter={(value) => {
                  if (value == null) return `- *`;
                  return getFormattedPrice(roundOff(value, 2), '£');
                }}
                labelFormatter={(value) => getFormattedPrice(value, '£')}
                width={propertyTypeChartWidth}
                height={propertyTypeChartHeight}
                lines={propertyTypesLineData}
                boldAxis
                hideYAxisLine
                bubbleAutoHeight
                lineTestID="propertyType"
                noDataLegend
                dottedPoints
                noDataText="No data available due to low sample size"
              />
            </LineChartWrapper>
          )}
        </LineChartContainer>
        {propertyTypesGrowth.length > 0 && (
          <StatsContent spaceAround small data-testid="propertyTypes-avg-price-growth">
            <Text mb={5} fontWeight="bold">
              Average price growth:
              {propertyTypesGrowth?.[0].label && (
                <Text as="span" display="block" fontWeight="bold">
                  {propertyTypesGrowth?.[0].label}
                </Text>
              )}
            </Text>
            {propertyTypesGrowth.map((avgPrice, i) => {
              const colour = MARKET_TRENDS_LINE_COLOURS[i];
              const value = avgPrice.value || 0;
              return (
                <StyleStats key={avgPrice.term} small>
                  <div>
                    <Text mb={5}>{avgPrice.term}</Text>
                    <Text fontSize={isMobile ? 24 : 30} color={colour}>
                      {avgPrice.change == null ? (
                        <span>- *</span>
                      ) : (
                        <>
                          {roundOff((avgPrice.change || 0) * 100, 1)}%{' '}
                          <Text as="span" fontSize={14} color={colour}>
                            ({value < 0 && '-'}
                            {value > 0 && '+'}
                            {getFormattedPrice(roundOff(value, 0), '£')})
                          </Text>
                        </>
                      )}
                    </Text>
                  </div>
                </StyleStats>
              );
            })}
            {propertyTypesGrowth.some((avgPrice) => avgPrice.change == null) && (
              <Text fontSize={12} style={{ marginTop: 'auto' }}>
                *Undisclosed due to low sample size
              </Text>
            )}
          </StatsContent>
        )}
      </Content>
      <LineChartHeader width={bedroomsChartWidth}>
        <SubHeading
          fontWeight="normal"
          color={theme.primary}
          align="center"
          data-testid="bedrooms-heading"
          mb={20}
          mt={60}
        >
          {bedroomText}
        </SubHeading>
        <Checkboxes>
          {bedroomOpts.map(({ label }, i) => (
            <Checkbox
              key={label}
              name={label!}
              onValueChange={(checked) =>
                setBedrooms((prev) => {
                  if (checked) return [...prev, label!];
                  return prev.filter((item) => item !== label!);
                })
              }
              value={bedrooms.includes(label!)}
              label={
                <MarketTrendsCheckboxLabel colour={MARKET_TRENDS_LINE_COLOURS[i]}>{label}</MarketTrendsCheckboxLabel>
              }
              testID={`${label}-bedroom-checkbox`}
            />
          ))}
        </Checkboxes>
      </LineChartHeader>
      <Content mt={40} mobGap={20}>
        <LineChartContainer>
          {!bedroomsLineData?.length ? (
            <Box mt={bedroomsGrowth.length ? 0 : 20}>
              <LoadingBox
                data-testid="bedroom-no-data"
                bg={theme.backgroundLighter}
                minHeight={bedroomsChartHeight + 15}
              >
                <Text mt={15} mb={15} align="center">
                  Select a number of bedrooms to view average price history
                </Text>
              </LoadingBox>
            </Box>
          ) : (
            <LineChartWrapper data-testid="bedrooms-line-chart" hasGrowth={!!bedroomsGrowth.length}>
              <LineChart
                bubbleTextFormatter={(value) => {
                  if (value == null) return `- *`;
                  return getFormattedPrice(roundOff(value, 2), '£');
                }}
                labelFormatter={(value) => getFormattedPrice(value, '£')}
                width={bedroomsChartWidth}
                height={bedroomsChartHeight}
                lines={bedroomsLineData}
                boldAxis
                hideYAxisLine
                bubbleAutoHeight
                lineTestID="bedroom"
                noDataLegend
                dottedPoints
                noDataText="No data available due to low sample size"
              />
            </LineChartWrapper>
          )}
        </LineChartContainer>
        {bedroomsGrowth.length > 0 && (
          <StatsContent spaceAround small data-testid="bedrooms-avg-price-growth">
            <Text mb={5} fontWeight="bold">
              Average price growth:
              {bedroomsGrowth?.[0].label && (
                <Text as="span" display="block" fontWeight="bold">
                  {bedroomsGrowth?.[0].label}
                </Text>
              )}
            </Text>
            {bedroomsGrowth.map((avgPrice, i) => {
              const colour = MARKET_TRENDS_LINE_COLOURS[i];
              const value = avgPrice.value || 0;
              return (
                <StyleStats key={avgPrice.term} small>
                  <div>
                    <Text mb={5}>{avgPrice.term}</Text>
                    <Text fontSize={isMobile ? 24 : 30} color={colour}>
                      {avgPrice.change == null ? (
                        <span>- *</span>
                      ) : (
                        <>
                          {roundOff((avgPrice.change || 0) * 100, 1)}%{' '}
                          <Text as="span" fontSize={14} color={colour}>
                            ({value < 0 && '-'}
                            {value > 0 && '+'}
                            {getFormattedPrice(roundOff(value, 0), '£')})
                          </Text>
                        </>
                      )}
                    </Text>
                  </div>
                </StyleStats>
              );
            })}
            {bedroomsGrowth.some((avgPrice) => avgPrice.change == null) && (
              <Text fontSize={12} style={{ marginTop: 'auto' }}>
                *Undisclosed due to low sample size
              </Text>
            )}
          </StatsContent>
        )}
      </Content>
    </div>
  );
};

export default MarketTrendsCharts;
